import {
  distanceBetween,
  type Vec2,
  vec2,
  add as vecAdd,
  lerp as vecLerp,
  normalize as vecNorm,
  rotate as vecRotate,
  scale as vecScale,
  sub as vecSub,
} from '@4bitlabs/vec2';
import { clamp, lerp } from './maths.js';
import { type GaussianRngFn, unsafeGaussRng } from './rng.js';
import type { WindingOrder } from './winding-order.js';

export interface DistortSegmentOptions {
  gaussRng?: GaussianRngFn;
  midPointFn?: (len: number, weights: [w0: number, w1: number]) => number;
  thetaFn?: (len: number, weight: number) => number;
  magnitudeFn?: (len: number, weight: number) => number;
  windingOrder?: WindingOrder;
}

export function distortSegment(
  p0: Vec2,
  p1: Vec2,
  w0: number,
  w1: number,
  options: DistortSegmentOptions = {},
): [Vec2, number] {
  const {
    windingOrder = 'cw',
    gaussRng = unsafeGaussRng,
    thetaFn = () => gaussRng(0, Math.PI / 12),
    midPointFn = () => clamp(0.001, 0.999, gaussRng(0.5, 0.4 / 3)),
    magnitudeFn = (len, weight) => {
      const scale = gaussRng(0, len / 3);
      const s2 = scale < 0 ? scale / 5 : scale;
      return s2 * weight;
    },
  } = options;

  const direction = windingOrder === 'cw' ? Math.PI / 2 : -Math.PI / 2;

  const len = distanceBetween(p0, p1);
  const mid = midPointFn(len, [w0, w1]);
  const midPoint = vecLerp(p0, p1, mid);
  const weight = lerp(w0, w1, mid);

  // Find tangent
  const tan = vec2();
  vecSub(p1, p0, tan);
  vecNorm(tan, tan);

  const theta = direction + thetaFn(len, weight);
  vecRotate(tan, theta, tan);

  // Find dist
  const magnitude = magnitudeFn(len, weight);
  vecScale(tan, magnitude, tan);
  vecAdd(midPoint, tan, midPoint);

  return [midPoint, weight];
}
